import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppIcon from "../components/WhatsAppIcon";
import { WHATSAPP_URL } from "../lib/constants";
import { usePageMeta } from "../lib/usePageMeta";
import {
  GraduationCap, HeartHandshake, Lightbulb, Compass, Globe2, ShieldCheck,
  FileCheck2, ClipboardList, Award, Bus, BookOpen, Rocket, Users, Leaf,
  ArrowRight, Trophy, Brain, Landmark, Microscope, TreePine, Building2, PaintbrushVertical,
} from "lucide-react";
import LogoMarquee, { ESCOLAS_LOGOS } from "../components/LogoMarquee";
import { MarcasBlock } from "./Portfolio";
import "./Portfolio.css";
import SchoolQuoteModal from "../components/SchoolQuoteModal";

// School-focused hero (students on a learning trip). Kept as remote assets so the
// component stays self-contained; swap to Unsplash uploads later if we want to
// go fully offline.
const HERO_IMG = "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1600&q=70";
const CLASSROOM_IMG = "https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=900&q=70";
const FIELD_IMG = "https://images.unsplash.com/photo-1516339901601-2e1b62dc0c45?auto=format&fit=crop&w=900&q=70";
const CTA_IMG = "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1600&q=70";

const PILLARS = [
  {
    icon: HeartHandshake,
    title: "Vivência Altruísta",
    body: "Encontros com comunidades tradicionais, oficinas com artesãos locais, plantio ecológico e ações de voluntariado supervisionado. Os alunos aprendem que gente vale mais do que produto.",
  },
  {
    icon: Brain,
    title: "Desenvolvimento Humano",
    body: "Círculos de escuta, dinâmicas socioemocionais e trabalhos em grupo mediados por educadores. Foco em liderança jovem, autoconhecimento, empatia e comunicação não violenta.",
  },
  {
    icon: Rocket,
    title: "Noções de Empreendedorismo",
    body: "Oficinas com pequenos negócios locais, visita a startups, ideação e prototipagem em campo. Aluno volta com um projeto autoral debaixo do braço, não só com fotos no celular.",
  },
  {
    icon: Compass,
    title: "Curadoria Pedagógica",
    body: "Cada roteiro é desenhado com o coordenador da escola alinhado ao currículo — biologia, história, geografia, sociologia, sustentabilidade — com material de apoio pré e pós-viagem.",
  },
  {
    icon: Leaf,
    title: "Educação Ambiental Prática",
    body: "Trilhas com biólogos, oficinas de compostagem, mergulho responsável e visita a áreas de preservação. A pauta ESG deixa de ser slide e vira experiência sentida.",
  },
  {
    icon: Trophy,
    title: "Formação Cidadã",
    body: "Debates sobre políticas públicas, patrimônio histórico e diversidade cultural. Turma volta com repertório real pra ENEM, redação e projeto de vida.",
  },
];

const AUDIENCES = [
  { title: "Ensino Fundamental II (6º ao 9º)", body: "Roteiros curtos (2 a 4 dias) com foco em socialização, meio ambiente e história do Brasil." },
  { title: "Ensino Médio & Técnico", body: "Viagens de 4 a 8 dias com pauta de empreendedorismo, protagonismo juvenil e formação profissional." },
  { title: "Universidades", body: "Intercâmbios de campo, visitas técnicas nacionais e internacionais, missões acadêmicas e imersão cultural." },
  { title: "Formatura & Ano de Conclusão", body: "Formatura cultural com curadoria — muito além do resort. Experiências que marcam, seguras e com contrato.", link: "/formaturas", linkLabel: "Ver viagens de formatura →" },
];

const AGENDA = [
  "Reunião de briefing com coordenação pedagógica e diretoria",
  "Material didático pré-viagem (apostila, roteiro, atividades de aquecimento)",
  "Guias e monitores certificados 1 pra cada 12 alunos",
  "Kit primeiros socorros, seguro-viagem e brigadista em cada grupo",
  "Roda de fechamento e relatório pós-viagem para pais e escola",
  "Contrato formal, nota fiscal para a instituição e Cadastur ativo",
];

const TRUST = [
  { icon: ShieldCheck, title: "Cadastur ativo", body: "Ministério do Turismo." },
  { icon: FileCheck2, title: "Contrato formal", body: "Cláusulas alinhadas ao CDC e ECA." },
  { icon: ClipboardList, title: "Nota fiscal", body: "Emissão para a instituição." },
  { icon: Award, title: "Empresa regularizada", body: "CNPJ 37.476.039/0001-90." },
];

export default function Educacional() {
  const [quoteOpen, setQuoteOpen] = React.useState(false);
  usePageMeta(
    "Excursões Escolares · Viagens Orion Educacional",
    "Excursões escolares com curadoria pedagógica: vivência altruísta, desenvolvimento humano, empreendedorismo e educação ambiental. Empresa Cadastur com contrato, NF e monitores certificados. Do fundamental à universidade."
  );

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1" data-testid="educacional-page">
        {/* HERO */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#1c3a2e] via-[#2C1139] to-[#0d0413] text-white">
          <span className="absolute inset-0 opacity-30" aria-hidden style={{
            backgroundImage: `url(${HERO_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }} />
          <span className="absolute inset-0 bg-gradient-to-b from-[#2C1139]/95 via-[#2C1139]/75 to-[#0d0413]/85" aria-hidden />
          <span className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-orion-gold/25 blur-3xl" aria-hidden />

          <div className="relative container-orion py-16 md:py-28 max-w-4xl text-center mx-auto">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-5">
              <GraduationCap size={13} /> Excursões Escolares · Orion Educacional
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1.02] mx-auto">
              A viagem escolar que forma <span className="text-orion-gold">gente inteira</span>.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/85 mx-auto leading-relaxed">
              Do fundamental à universidade, operamos excursões que somam ao currículo:
              vivência altruísta, desenvolvimento humano e noções de empreendedorismo —
              com guias certificados, contrato, nota fiscal e Cadastur.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <button
                onClick={() => setQuoteOpen(true)}
                className="inline-flex items-center gap-2 bg-orion-gold text-orion-purple px-7 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#c19a3f] transition-all shadow-[0_14px_38px_rgba(216,167,73,0.45)] hover:-translate-y-0.5"
                data-testid="edu-cta-wa"
              >
                <ClipboardList size={14} /> Solicitar Cotação Escolar
              </button>
              <a href="#experiencias" className="inline-flex items-center gap-2 border border-white/30 text-white px-7 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:border-orion-gold hover:text-orion-gold transition-colors">
                Ver Experiências <ArrowRight size={14} />
              </a>
            </div>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4 md:gap-8 text-[10px] uppercase tracking-[0.3em] text-white/75">
              <span className="inline-flex items-center gap-2"><ShieldCheck size={12} className="text-orion-gold" /> Cadastur ativo</span>
              <span className="inline-flex items-center gap-2"><FileCheck2 size={12} className="text-orion-gold" /> Contrato + ECA</span>
              <span className="inline-flex items-center gap-2"><ClipboardList size={12} className="text-orion-gold" /> Nota fiscal</span>
              <span className="inline-flex items-center gap-2"><Award size={12} className="text-orion-gold" /> Monitores 1:12</span>
            </div>
          </div>
        </section>

        {/* TRUST STRIP */}
        <section className="border-y border-[#EFEAE0] bg-[#FAF7F0]">
          <div className="container-orion py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {TRUST.map((t, i) => (
              <div key={i} className="flex flex-col items-center gap-2" data-testid={`edu-trust-${i}`}>
                <t.icon size={22} className="text-orion-orange" />
                <div className="font-serif text-orion-purple text-sm md:text-base">{t.title}</div>
                <div className="text-[10px] md:text-xs uppercase tracking-[0.15em] text-[#594A63] leading-snug">{t.body}</div>
              </div>
            ))}
          </div>
        </section>

        {/* PILLARS */}
        <section id="experiencias" className="container-orion py-20 md:py-28">
          <div className="max-w-2xl mx-auto mb-14 text-center">
            <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-3">Nossas Vertentes</div>
            <h2 className="font-serif text-3xl md:text-5xl text-orion-purple leading-tight">
              Muito além de sair da escola.
            </h2>
            <p className="mt-4 text-[#594A63] text-base md:text-lg leading-relaxed">
              Cada excursão é pensada como uma extensão da sala de aula — com material
              didático pré e pós, rodas de conversa e atividades que costuram teoria e
              território. A viagem termina, o aprendizado fica.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => (
              <div
                key={i}
                className="group border border-[#EFEAE0] p-7 hover:border-orion-orange hover:shadow-[0_18px_40px_rgba(44,17,57,0.12)] transition-all bg-white text-center"
                data-testid={`edu-pillar-${i}`}
              >
                <p.icon size={30} className="text-orion-orange mb-4 mx-auto group-hover:scale-110 transition-transform" strokeWidth={1.6} />
                <h3 className="font-serif text-xl text-orion-purple mb-3 leading-snug">{p.title}</h3>
                <p className="text-sm text-[#594A63] leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* PHILOSOPHY / OPERATION */}
        <section className="bg-[#1c3a2e] text-white overflow-hidden">
          <div className="container-orion py-20 md:py-28 grid lg:grid-cols-2 gap-14 items-center">
            <div className="grid grid-cols-2 gap-4 order-2 lg:order-1">
              <img src={CLASSROOM_IMG} alt="Sala de aula em campo" loading="lazy" className="w-full aspect-[3/4] object-cover rounded-lg shadow-[0_18px_40px_rgba(0,0,0,0.35)] mt-8" />
              <img src={FIELD_IMG} alt="Trabalho de campo com alunos" loading="lazy" className="w-full aspect-[3/4] object-cover rounded-lg shadow-[0_18px_40px_rgba(0,0,0,0.35)]" />
            </div>
            <div className="order-1 lg:order-2">
              <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-3">Como operamos</div>
              <h2 className="font-serif text-3xl md:text-5xl leading-[1.05]">
                Segurança de ponta a ponta, sem improvisos.
              </h2>
              <p className="mt-5 text-white/85 text-base md:text-lg leading-relaxed">
                Operamos com o padrão que a sua diretoria pediria pra própria filha:
                monitores capacitados, protocolos ECA, seguro-viagem, brigadista embarcado
                e comunicação em tempo real com os responsáveis.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-white/85">
                {AGENDA.map((li, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-orion-gold mt-1">›</span> {li}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* AUDIENCES */}
        <section className="container-orion py-20 md:py-28">
          <div className="max-w-2xl mx-auto mb-12 text-center">
            <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-3">Para quem</div>
            <h2 className="font-serif text-3xl md:text-5xl text-orion-purple leading-tight">
              Do sexto ano ao TCC — cada fase merece um roteiro.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {AUDIENCES.map((a, i) => (
              <div key={i} className="p-7 border-t-4 border-orion-orange bg-[#FAF7F0] text-center" data-testid={`edu-audience-${i}`}>
                <BookOpen size={26} className="text-orion-orange mb-3 mx-auto" strokeWidth={1.6} />
                <h3 className="font-serif text-xl text-orion-purple mb-2 leading-snug">{a.title}</h3>
                <p className="text-sm text-[#594A63] leading-relaxed">{a.body}</p>
                {a.link && (
                  <Link
                    to={a.link}
                    className="mt-4 inline-block text-[11px] uppercase tracking-[0.22em] font-bold text-orion-orange hover:text-[#E04828] transition-colors"
                    data-testid={`edu-audience-link-${i}`}
                  >
                    {a.linkLabel || "Saiba mais"}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* BUS PARTNERSHIP + SPECIALIZED GUIDE — differentiator strip */}
        <section className="bg-[#2C1139] text-white overflow-hidden" data-testid="edu-highlight">
          <div className="container-orion py-20 md:py-28 grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="text-[11px] uppercase tracking-[0.32em] text-orion-gold font-semibold mb-3">
                Diferencial Orion Educacional
              </div>
              <h2 className="font-serif text-3xl md:text-5xl leading-[1.05]">
                Parceria com <em className="not-italic text-orion-gold">Empresas de Transporte</em> com o melhor R$/km rodado do mercado.
              </h2>
              <p className="mt-5 text-white/85 text-base md:text-lg leading-relaxed">
                A Orion mantém <strong>contratos anuais fechados com empresas de ônibus com frota nova</strong> —
                o que nos permite entregar tarifas por quilômetro imbatíveis para excursões
                escolares recorrentes, mais conforto e segurança. E cada saída vai com
                <strong> guia especializado</strong> — treinado em desenvolvimento infantojuvenil,
                protocolos ECA e primeiros socorros.
              </p>
              <ul className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm text-white/90">
                {[
                  { icon: Bus, t: "Melhor R$/km rodado", d: "Contratos anuais com empresas de ônibus parceiras — a escola economiza no ano inteiro." },
                  { icon: ShieldCheck, t: "Ônibus vistoriados", d: "Frota nova, CRLV e ANTT ativos, motorista dobrado em trechos +7h." },
                  { icon: Users, t: "Guia especializado", d: "Foco em desenvolvimento das crianças, monitoria 1:12 e primeiros socorros." },
                  { icon: Award, t: "Contrato anual escolar", d: "Vantagem escalonada por saídas do ano — quanto mais viagens, melhor a tarifa." },
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-3" data-testid={`edu-highlight-item-${i}`}>
                    <span className="shrink-0 w-9 h-9 rounded-md border border-orion-gold/30 bg-orion-gold/10 flex items-center justify-center">
                      <f.icon size={16} className="text-orion-gold" strokeWidth={1.8} />
                    </span>
                    <span>
                      <strong className="text-white block leading-tight">{f.t}</strong>
                      <span className="text-white/70 text-[13px] leading-snug">{f.d}</span>
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-9">
                <button
                  onClick={() => setQuoteOpen(true)}
                  className="inline-flex items-center gap-2 bg-orion-gold text-orion-purple px-7 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#c19a3f] transition-all shadow-[0_14px_38px_rgba(216,167,73,0.45)] hover:-translate-y-0.5"
                  data-testid="edu-cta-highlight"
                >
                  <ClipboardList size={14} /> Solicitar Cotação Escolar
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img src={CLASSROOM_IMG} alt="Alunos em atividade de campo" loading="lazy" className="w-full aspect-[3/4] object-cover rounded-lg shadow-[0_18px_40px_rgba(0,0,0,0.35)]" />
              <img src={FIELD_IMG} alt="Guia especializado com alunos" loading="lazy" className="w-full aspect-[3/4] object-cover rounded-lg shadow-[0_18px_40px_rgba(0,0,0,0.35)] mt-8" />
            </div>
          </div>
        </section>

        {/* SCHOOLS ALREADY ATTENDED — full-size marquee (same as Corporativo) */}
        <MarcasBlock
          id="escolas"
          eyebrow="◈ Escolas que caminham com a Orion"
          title="Instituições que já viajaram com a gente"
          subtitle="Cada logo aqui é uma coordenação pedagógica que confiou na curadoria da Orion — e voltou pra confiar de novo."
          logos={ESCOLAS_LOGOS.map((s) => ({ name: s.name, logo: s.src }))}
        />

        {/* CTA */}
        <section className="relative bg-gradient-to-br from-orion-gold via-[#c19a3f] to-[#8a6d24] text-white overflow-hidden">
          <span className="absolute inset-0 opacity-25" aria-hidden style={{ backgroundImage: `url(${CTA_IMG})`, backgroundSize: "cover" }} />
          <span className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" aria-hidden />
          <div className="relative container-orion py-20 md:py-28 text-center max-w-3xl">
            <Bus size={30} className="mx-auto text-white mb-5" />
            <h2 className="font-serif text-3xl md:text-5xl leading-tight">
              Vamos desenhar a próxima excursão da sua escola?
            </h2>
            <p className="mt-5 text-white/95 text-base md:text-lg">
              Envie o nível de ensino, o número de alunos e o objetivo pedagógico. A curadoria
              da Orion volta em minutos com proposta didática + orçamento + segurança.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <button
                onClick={() => setQuoteOpen(true)}
                className="inline-flex items-center gap-2 bg-white text-orion-purple px-8 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-orion-cream transition-all hover:-translate-y-0.5 shadow-[0_14px_38px_rgba(0,0,0,0.25)]"
                data-testid="edu-cta-bottom"
              >
                <ClipboardList size={14} /> Solicitar Cotação Escolar
              </button>
              <Link
                to="/sobre"
                className="inline-flex items-center gap-2 border border-white/40 text-white px-8 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-white/10 transition-colors"
              >
                Conhecer a Orion
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      {quoteOpen && <SchoolQuoteModal onClose={() => setQuoteOpen(false)} variant="educacional" />}
    </div>
  );
}
